import { isAnyOf, type TypedStartListening } from '@reduxjs/toolkit';

import { authActions } from '../../flux/auth';
import { bindPromiseAction } from '../../flux/core/actionHelpers';
import { receiptsActions, receiptsSelectors } from '../../flux/receipts';
import { hasReceiptAttachment } from '../../flux/receipts/receiptAttachmentChange';
import { getReceiptSessionEpoch, invalidateReceiptSession } from '../../flux/receipts/receiptSessionEpoch';
import { isProcessingReceipt } from '../../flux/receipts/receiptsPredicates';
import { RECEIPTS_POLL_INTERVAL_MS, type RECEIPTS_REDUCER_KEY } from '../../flux/receipts/receiptsTypes';
import { transactionsActions, transactionsSelectors } from '../../flux/transactions';
import type { TransactionRecord } from '../../flux/transactions/transactionsTypes';
import { getLogger } from '../../utils/core';
import type { AppDispatch } from '../../utils/redux-store';
import type RootState from '../../utils/redux-store/rootState';

type RequiredState = Pick<RootState, typeof RECEIPTS_REDUCER_KEY> &
  Parameters<typeof transactionsSelectors.getTransactionById>[0];
type AppStartListening = TypedStartListening<RequiredState, AppDispatch>;
type ListenerApi = Parameters<Parameters<AppStartListening>[0]['effect']>[1];

export type ReceiptTargetedUploadListenerOptions = {
  getActiveUploadIds: () => ReadonlyArray<string>;
};

const log = getLogger('store/listeners/receiptTargetedUpload');

const isSessionBoundary = isAnyOf(authActions.authLogout, authActions.authSelectDataset);
const isTransactionSaveSuccess = isAnyOf(
  transactionsActions.updateTransactionSuccess,
  transactionsActions.batchTransactionsSuccess,
);
const isTargetedLinkRequest = isAnyOf(
  receiptsActions.linkTargetedReceiptToTransaction.pending,
  receiptsActions.linkTargetedReceiptToTransaction.fulfilled,
  receiptsActions.linkTargetedReceiptToTransaction.rejected,
);

const asTransactions = (payload: unknown): TransactionRecord[] =>
  (Array.isArray(payload) ? payload : [payload]).filter(Boolean) as TransactionRecord[];

/** Polls editor-uploaded receipts and repairs a link a later save drops; the editor's Update does the link. */
export const setupReceiptTargetedUploadListener = (
  startAppListening: AppStartListening,
  { getActiveUploadIds }: ReceiptTargetedUploadListenerOptions,
): void => {
  startAppListening({
    predicate: (action, currentState, previousState) =>
      action.type === 'INIT_LISTENERS' ||
      receiptsActions.fetchReceipts.fulfilled.match(action) ||
      receiptsActions.fetchReceipts.rejected.match(action) ||
      currentState.receiptsStore.entities !== previousState.receiptsStore.entities,
    async effect(_action, listenerApi) {
      const state = listenerApi.getState();
      const targetedReceipts = receiptsSelectors
        .selectAllReceipts(state)
        .filter((receipt) => Boolean(receipt.targetTransactionId));

      // Receipt updates restart one timer; a late response cannot leave an old timer polling.
      listenerApi.cancelActiveListeners();
      if (!targetedReceipts.some(isProcessingReceipt)) return;

      try {
        await listenerApi.delay(RECEIPTS_POLL_INTERVAL_MS);
        const currentState = listenerApi.getState();
        // A receipts fetch already in flight (such as Receipt Center's poll) covers this tick, and
        // its result restarts this timer.
        if (
          currentState.receiptsStore.isLoading ||
          !receiptsSelectors
            .selectAllReceipts(currentState)
            .some((receipt) => receipt.targetTransactionId && isProcessingReceipt(receipt))
        ) {
          return;
        }
        await listenerApi
          .dispatch(receiptsActions.fetchReceipts(receiptsSelectors.selectReceiptSyncParams(currentState)))
          .unwrap();
        listenerApi.dispatch(
          receiptsActions.expireStalePendingReceipts({
            now: Date.now(),
            activeUploadIds: getActiveUploadIds(),
          }),
        );
      } catch (error) {
        if (!listenerApi.signal.aborted) log.warn('Failed to poll targeted receipt:', error);
      }
    },
  });

  // Receipts linked from a transaction this session. A transaction save sends the full attachments
  // array, so a save that lands after the link can drop the attachment the receipt service added.
  const linkedReceiptIdByTransactionId = new Map<string, string>();
  const inFlightLinkRequestIds = new Set<string>();
  const staleLinkRequestIds = new Set<string>();
  let sessionEpoch = 0;

  const refreshLinkedData = async (listenerApi: ListenerApi, transactionId: string): Promise<void> => {
    const results = await Promise.allSettled([
      bindPromiseAction(listenerApi.dispatch, transactionsActions.getTransaction)({ id: transactionId }),
      listenerApi
        .dispatch(receiptsActions.fetchReceipts(receiptsSelectors.selectReceiptSyncParams(listenerApi.getState())))
        .unwrap(),
    ]);
    results.forEach((result) => {
      if (result.status === 'rejected') log.warn('Failed to refresh linked receipt data:', result.reason);
    });
  };

  /** Re-runs the idempotent link when a saved copy of the transaction no longer has the receipt. */
  const restoreReceiptAttachment = async (
    listenerApi: ListenerApi,
    transaction: TransactionRecord | undefined,
  ): Promise<void> => {
    const transactionId = transaction?.id;
    const receiptId = transactionId ? linkedReceiptIdByTransactionId.get(transactionId) : undefined;
    const receipt = receiptId ? receiptsSelectors.selectReceiptById(listenerApi.getState(), receiptId) : undefined;
    if (!transaction || !transactionId || receipt?.transactionId !== transactionId) return;
    if (hasReceiptAttachment(transaction, receipt)) return;

    const epoch = listenerApi.getState().receiptsStore.sessionEpoch;
    const storeEpoch = getReceiptSessionEpoch(listenerApi.dispatch);
    try {
      await listenerApi
        .dispatch(
          receiptsActions.repairReceiptAttachment({ id: receipt.id, transactionId, sessionEpoch: epoch, storeEpoch }),
        )
        .unwrap();
    } catch (error) {
      if (epoch !== listenerApi.getState().receiptsStore.sessionEpoch) return;
      log.error('Failed to restore a receipt attachment after a transaction save:', error);
      return;
    }
    if (epoch === listenerApi.getState().receiptsStore.sessionEpoch)
      await refreshLinkedData(listenerApi, transactionId);
  };

  startAppListening({
    matcher: isTargetedLinkRequest,
    effect(action) {
      if (receiptsActions.linkTargetedReceiptToTransaction.pending.match(action)) {
        inFlightLinkRequestIds.add(action.meta.requestId);
      } else {
        inFlightLinkRequestIds.delete(action.meta.requestId);
        // A stale fulfilled id is consumed by the refresh listener below; a rejected one is not.
        if (receiptsActions.linkTargetedReceiptToTransaction.rejected.match(action)) {
          staleLinkRequestIds.delete(action.meta.requestId);
        }
      }
    },
  });

  startAppListening({
    actionCreator: receiptsActions.linkTargetedReceiptToTransaction.fulfilled,
    async effect(action, listenerApi) {
      if (staleLinkRequestIds.delete(action.meta.requestId)) return;
      const { id, transactionId } = action.payload;
      if (!transactionId) return;

      linkedReceiptIdByTransactionId.set(transactionId, id);
      const epoch = sessionEpoch;
      await refreshLinkedData(listenerApi, transactionId);
      // A save that landed while the link was in flight shows up in the refreshed copy.
      if (epoch === sessionEpoch) {
        await restoreReceiptAttachment(
          listenerApi,
          transactionsSelectors.getTransactionById(listenerApi.getState(), transactionId),
        );
      }
    },
  });

  startAppListening({
    matcher: isTransactionSaveSuccess,
    async effect(action, listenerApi) {
      await Promise.all(
        asTransactions(action.payload).map((transaction) => restoreReceiptAttachment(listenerApi, transaction)),
      );
    },
  });

  startAppListening({
    matcher: isSessionBoundary,
    effect(_action, listenerApi) {
      sessionEpoch += 1;
      invalidateReceiptSession(listenerApi.dispatch);
      inFlightLinkRequestIds.forEach((requestId) => staleLinkRequestIds.add(requestId));
      inFlightLinkRequestIds.clear();
      linkedReceiptIdByTransactionId.clear();
      listenerApi.dispatch(receiptsActions.clearTransactionTargets());
    },
  });
};
